import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import {
  Container,
  Typography,
  Paper,
  Stack,
  FormControl,
  FormControlLabel,
  TextField,
  Checkbox,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Divider,
  IconButton,
  Slider,
} from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import TuneIcon from '@mui/icons-material/Tune';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { setResponses as setResponsesAction, setQuestionsPricing, setQuestionsOnceOffFee } from '../features/questions/responsesSlice';
import { setOrganisation } from '../features/auth/authSlice';
import { updatePrice } from '../services/priceApi';
import { bookkeepingQuestionData } from '../constants/bookkeepingQuestions';
import { calculateBookkeepingBronzePrice, calculateBookkeepingSilverPrice, calculateBookkeepingGoldPrice, calculateBookkeepingOnceOffFee } from '../utils/bookkeepingPricingCalculator';

// Default bookkeeping pricing modifier (base hourly rate: $100/hr)
const DEFAULT_BOOKKEEPING_PRICING_MODIFIER = 100;

const flattenQuestions = (questions) => {
  const collected = [];
  const traverse = (items) => {
    items.forEach((question) => {
      collected.push(question);
      if (question.children) {
        traverse(question.children);
      }
    });
  };
  traverse(questions);
  return collected;
};

const buildInitialState = () => {
  const flat = flattenQuestions(bookkeepingQuestionData);
  return flat.reduce((acc, question) => {
    if (question.type === 'radio') {
      acc[question.id] = '';
      return acc;
    }

    if (question.type === 'multiRadio') {
      acc[question.id] = {};
      return acc;
    }

    if (question.type === 'checkbox') {
      acc[question.id] = question.options.reduce((optionState, option) => {
        optionState[option.value] = false;
        return optionState;
      }, {});
      return acc;
    }

    if (question.type === 'number' || question.type === 'text') {
      acc[question.id] = '';
      return acc;
    }

    if (question.type === 'inputGroup') {
      acc[question.id] = question.options.reduce((groupState, option) => {
        if (option.control === 'checkbox') {
          groupState[option.value] = false;
        } else {
          groupState[option.value] = '';
        }
        return groupState;
      }, {});
      return acc;
    }

    if (question.type === 'dynamicList') {
      acc[question.id] = [];
      return acc;
    }

    if (question.type === 'slider') {
      acc[question.id] = typeof question.defaultValue === 'number' ? question.defaultValue : 0;
      return acc;
    }

    acc[question.id] = '';
    return acc;
  }, {});
};

const loadResponsesFromStorage = () => {
  try {
    const stored = localStorage.getItem('bookkeeping_responses');
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

export default function BookkeepingQuestions() {
  const navigate = useNavigate();
  const storeResponses = useSelector((state) => state.responses);
  const activePriceId = useSelector((state) => state.responses?.activePriceId);
  const clientName = useSelector((state) => state.responses?.clientName || '');
  const initialState = useMemo(() => {
    const built = buildInitialState();
    // First try to load from localStorage
    const storedResponses = loadResponsesFromStorage();
    if (Object.keys(storedResponses).length) {
      return { ...built, ...storedResponses };
    }
    // Then try to load from Redux
    if (storeResponses && Object.keys(storeResponses).length) {
      const merged = { ...built, ...storeResponses };
      return merged;
    }
    return built;
  }, [storeResponses]);
  const [responses, setResponses] = useState(initialState);
  const [requireQ1Message, setRequireQ1Message] = useState(false);
  
  // Get bookkeeping pricing modifier from organisation
  const organisation = useSelector((state) => state.auth.organisation);
  const baseBookkeepingPricingModifier = organisation?.bookkeepingPricingModifier ?? DEFAULT_BOOKKEEPING_PRICING_MODIFIER;
  // Apply per-quote price adjustment from slider (q30): -10% to +20%
  const priceAdjustmentPercent = Math.max(-10, Math.min(20, Number(responses.q30) || 0));
  const bookkeepingPricingModifier = baseBookkeepingPricingModifier * (1 + priceAdjustmentPercent / 100);
  
  const [focusedQuestion, setFocusedQuestion] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const saveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Sync local state when a new price is created or loaded (activePriceId changes)
  const prevActivePriceIdRef = useRef(activePriceId);
  useEffect(() => {
    if (prevActivePriceIdRef.current !== activePriceId) {
      setResponses(initialState);
      lastSavedRef.current = null; // Reset saved ref to allow fresh saves
      prevActivePriceIdRef.current = activePriceId;
    }
  }, [activePriceId, initialState]);

  // Auto-save debounced function
  const autoSave = useCallback(async (currentResponses) => {
    if (!activePriceId) return;
    
    // Filter out non-question keys before saving
    const nonQuestionKeys = ['questionsPricing', 'serviceCatalogPricing', 'serviceSelections', 'questionsOnceOffFee', 'serviceCatalogOnceOffFee', 'clientName', 'activePriceId'];
    const questionResponses = {};
    Object.entries(currentResponses).forEach(([key, value]) => {
      if (!nonQuestionKeys.includes(key)) {
        questionResponses[key] = value;
      }
    });

    const dataStr = JSON.stringify(questionResponses);
    if (dataStr === lastSavedRef.current) return; // No changes

    try {
      setSaveStatus('saving');
      const totalMonthly = calculateBookkeepingSilverPrice(currentResponses, bookkeepingPricingModifier);
      const totalOnceOff = calculateBookkeepingOnceOffFee(currentResponses, bookkeepingPricingModifier);
      // Extract revenue segment from Q1 (annual revenue question)
      const revenueSegment = currentResponses?.q1 || undefined;
      await updatePrice(activePriceId, {
        questionResponses,
        questionsPricing: totalMonthly,
        questionsOnceOffFee: totalOnceOff,
        revenueSegment,
      });
      lastSavedRef.current = dataStr;
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  }, [activePriceId, bookkeepingPricingModifier]);

  // Debounced auto-save when responses change.
  // Flush pending save on unmount so navigating away doesn't discard answers.
  const pendingResponsesRef = useRef(null);
  useEffect(() => {
    if (!activePriceId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    pendingResponsesRef.current = responses;
    saveTimerRef.current = setTimeout(() => {
      autoSave(responses);
      pendingResponsesRef.current = null;
    }, 1500);
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        if (pendingResponsesRef.current) {
          autoSave(pendingResponsesRef.current);
          pendingResponsesRef.current = null;
        }
      }
    };
  }, [responses, activePriceId, autoSave]);

  const dispatch = useDispatch();
  
  // Fetch organisation data to ensure bookkeepingPricingModifier is available
  useEffect(() => {
    const fetchOrganisation = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000/v1'}/organisations/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          dispatch(setOrganisation({
            organisation: data,
            isOwner: data.isOwner || false,
          }));
        }
      } catch {}
    };
    
    fetchOrganisation();
  }, [dispatch]);
  
  useEffect(() => {
    dispatch(setResponsesAction(responses));
    if (responses.q1 && requireQ1Message) {
      setRequireQ1Message(false);
    }
  }, [responses, dispatch, requireQ1Message]);

  useEffect(() => {
    if (responses.q1) {
      localStorage.setItem('selectedRevenueSegment', responses.q1);
    }
  }, [responses.q1]);

  const handleRadioChange = (questionId) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({ ...prev, [questionId]: event.target.value }));
  };

  const handleNumberChange = (questionId) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({ ...prev, [questionId]: event.target.value }));
  };

  const handleTextChange = (questionId) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({ ...prev, [questionId]: event.target.value }));
  };

  const handleCheckboxChange = (questionId, optionValue) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [optionValue]: event.target.checked },
    }));
  };

  const handleInputGroupChange = (questionId, optionValue) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [optionValue]: event.target.value },
    }));
  };

  const handleInputGroupCheckboxChange = (questionId, optionValue) => (event) => {
    setFocusedQuestion(questionId);
    setResponses((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [optionValue]: event.target.checked },
    }));
  };

  const renderQuestion = (question, depth = 0, questionNumber = null) => {
    if (question.showWhen && !question.showWhen(responses)) {
      return null;
    }

    // Format prompt with dynamic number if provided
    const displayPrompt = questionNumber !== null 
      ? `${questionNumber}. ${question.prompt}`
      : question.prompt;

    return (
      <div key={question.id} style={{ marginLeft: depth > 0 ? `${depth * 32}px` : 0, marginTop: depth > 0 ? '8px' : '20px' }}>
        <Paper
          elevation={0}
          onFocus={() => setFocusedQuestion(question.id)}
          onBlur={() => setFocusedQuestion(null)}
          sx={{
            p: 2,
            borderRadius: '12px',
            position: 'relative',
            zIndex: depth > 0 ? 1 : 0,
            backgroundColor: '#ffffff',
            transition: 'all 0.2s ease-in-out',
            boxShadow: focusedQuestion === question.id ? (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}` : 'none',
            '&:hover': {
              backgroundColor: '#ffffff',
              boxShadow: focusedQuestion === question.id ? (theme) => `inset 0 0 0 2px ${theme.palette.primary.main}` : '14px 17px 40px 4px rgba(112, 144, 176, 0.12)',
            },
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '1rem' }}>{displayPrompt}</Typography>
            {question.description && (
              <Typography variant="caption" display="block" sx={{ fontWeight: 'normal', mt: '-4px !important', mb: 0.5, fontSize: '0.95rem' }}>{question.description}</Typography>
            )}
            
            {question.type === 'radio' && (
              <ToggleButtonGroup
                value={responses[question.id]}
                exclusive
                onChange={(event, newValue) => {
                  if (newValue !== null) {
                    setFocusedQuestion(question.id);
                    handleRadioChange(question.id)({ target: { value: newValue } });
                  }
                }}
                size="medium"
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  display: 'flex',
                }}
                disabled={question.id !== 'q1' && !responses.q1}
                onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
              >
                {question.options.map((option) => {
                  if (option.showWhen && !option.showWhen(responses)) {
                    return null;
                  }
                  return (
                    <ToggleButton 
                      key={option.value} 
                      value={option.value}
                      sx={{
                        minWidth: '120px',
                        flex: '0 1 120px',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        minHeight: '72px',
                        py: 1.2,
                        px: 1.5,
                        fontSize: '0.9rem',
                      }}
                      disabled={question.id !== 'q1' && !responses.q1}
                      onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>{option.label}</Typography>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>
            )}

            {question.type === 'multiRadio' && (
              <ToggleButtonGroup
                value={Object.keys(responses[question.id] || {}).filter(k => responses[question.id][k])}
                onChange={(event, newValue) => {
                  setFocusedQuestion(question.id);
                  const clickedValue = event.currentTarget.value;
                  
                  // Handle clearOnValue behavior
                  if (question.clearOnValue && clickedValue === question.clearOnValue) {
                    // Clicking "None" clears all selections
                    setResponses((prev) => ({
                      ...prev,
                      [question.id]: { [question.clearOnValue]: true },
                    }));
                  } else if (question.clearOnValue) {
                    // Clicking any other value - remove clearOnValue and toggle the clicked value
                    const current = responses[question.id] || {};
                    const newState = { ...current };
                    delete newState[question.clearOnValue];
                    
                    // Handle mutually exclusive options (e.g., basQuarterly and basMonthly)
                    if (question.mutuallyExclusive && question.mutuallyExclusive.includes(clickedValue)) {
                      question.mutuallyExclusive.forEach(v => {
                        if (v !== clickedValue) delete newState[v];
                      });
                    }
                    
                    newState[clickedValue] = !current[clickedValue];
                    setResponses((prev) => ({
                      ...prev,
                      [question.id]: newState,
                    }));
                  } else {
                    // Standard toggle behavior
                    setResponses((prev) => ({
                      ...prev,
                      [question.id]: {
                        ...(prev[question.id] || {}),
                        [clickedValue]: !(prev[question.id]?.[clickedValue]),
                      },
                    }));
                  }
                }}
                size="medium"
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  display: 'flex',
                }}
                disabled={!responses.q1}
                onClick={!responses.q1 ? () => setRequireQ1Message(true) : undefined}
              >
                {question.options.map((option) => (
                  <ToggleButton 
                    key={option.value} 
                    value={option.value}
                    selected={responses[question.id]?.[option.value] || false}
                    sx={{
                      minWidth: '120px',
                      flex: '0 1 120px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      minHeight: '72px',
                      py: 1.2,
                      px: 1.5,
                      fontSize: '0.9rem',
                    }}
                    disabled={!responses.q1}
                    onClick={!responses.q1 ? () => setRequireQ1Message(true) : undefined}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>{option.label}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {question.type === 'checkbox' && (
              <Stack spacing={1}>
                {question.options.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={responses[question.id]?.[option.value] || false}
                        onChange={(e) => {
                          setFocusedQuestion(question.id);
                          handleCheckboxChange(question.id, option.value)(e);
                        }}
                        size="small"
                        disabled={question.id !== 'q1' && !responses.q1}
                        onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                        sx={{
                          '&.Mui-disabled': {
                            opacity: 0.6,
                          },
                        }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: question.id !== 'q1' && !responses.q1 ? '#ccc' : '#666', fontWeight: 500 }}>{option.label}</Typography>}
                    disabled={question.id !== 'q1' && !responses.q1}
                    onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                    sx={{
                      m: 0,
                      transition: 'all 0.2s ease-in-out',
                    }}
                  />
                ))}
              </Stack>
            )}

            {question.type === 'mixed' && (
              <ToggleButtonGroup
                value={responses[question.id]?.basOption || ''}
                exclusive
                size="medium"
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  display: 'flex',
                }}
                disabled={question.id !== 'q1' && !responses.q1}
                onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
              >
                {/* BAS Options - mutually exclusive */}
                {question.basOptions.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    onChange={() => {
                      setFocusedQuestion(question.id);
                      setResponses((prev) => ({
                        ...prev,
                        [question.id]: {
                          ...prev[question.id],
                          basOption: option.value,
                          basQuarterly: option.value === 'basQuarterly',
                          basMonthly: option.value === 'basMonthly',
                        },
                      }));
                    }}
                    sx={{
                      minWidth: '120px',
                      flex: '0 1 120px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      py: 1.2,
                      px: 1.5,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      color: '#555',
                      '&.Mui-selected': {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: '#fff',
                        borderColor: (theme) => theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      },
                      '&:hover': {
                        borderColor: '#999',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>{option.label}</Typography>
                  </ToggleButton>
                ))}

                {/* IAS Option - independent toggle */}
                {question.independentOptions.map((option) => (
                  <ToggleButton
                    key={option.value}
                    value={option.value}
                    selected={responses[question.id]?.iasMonthly || false}
                    onChange={() => {
                      setFocusedQuestion(question.id);
                      setResponses((prev) => ({
                        ...prev,
                        [question.id]: {
                          ...prev[question.id],
                          iasMonthly: !prev[question.id]?.iasMonthly,
                        },
                      }));
                    }}
                    sx={{
                      minWidth: '120px',
                      flex: '0 1 120px',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      minHeight: '72px',
                      py: 1.2,
                      px: 1.5,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      border: '2px solid #ddd',
                      color: '#555',
                      '&.Mui-selected': {
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: '#fff',
                        borderColor: (theme) => theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.dark,
                        },
                      },
                      '&:hover': {
                        borderColor: '#999',
                      },
                      '&.Mui-disabled': {
                        opacity: 0.6,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit' }}>{option.label}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {question.type === 'number' && (
              <div 
                onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                style={{ display: 'inline-block' }}
              >
                <TextField
                  type="number"
                  inputProps={{ min: 0 }}
                  value={responses[question.id]}
                  onChange={(e) => {
                    setFocusedQuestion(question.id);
                    handleNumberChange(question.id)(e);
                  }}
                  onFocus={() => setFocusedQuestion(question.id)}
                  placeholder="Enter number"
                  size="small"
                  disabled={question.id !== 'q1' && !responses.q1}
                  variant="outlined"
                  sx={{
                    maxWidth: '150px',
                    pointerEvents: question.id !== 'q1' && !responses.q1 ? 'none' : 'auto',
                    '& .MuiOutlinedInput-input::placeholder': {
                      opacity: 0.8,
                      color: '#666666',
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#002060',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(0, 32, 96, 0.1)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: '#ccc',
                        opacity: 0.6,
                        borderColor: '#ececec',
                      },
                    },
                    '& .MuiOutlinedInput-input:disabled': {
                      WebkitTextFillColor: '#ccc',
                    },
                    '& .MuiInputBase-input:disabled': {
                      WebkitTextFillColor: '#ccc',
                    },
                    '& .MuiFormLabel-root.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                />
              </div>
            )}

            {question.type === 'text' && (
              <div 
                onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                style={{ display: 'inline-block' }}
              >
                <TextField
                  type="text"
                  value={responses[question.id] || ''}
                  onChange={(e) => {
                    setFocusedQuestion(question.id);
                    handleTextChange(question.id)(e);
                  }}
                  onFocus={() => setFocusedQuestion(question.id)}
                  placeholder="Enter details"
                  size="small"
                  disabled={question.id !== 'q1' && !responses.q1}
                  variant="outlined"
                  sx={{
                    maxWidth: '400px',
                    width: '100%',
                    pointerEvents: question.id !== 'q1' && !responses.q1 ? 'none' : 'auto',
                    '& .MuiOutlinedInput-input::placeholder': {
                      opacity: 0.8,
                      color: '#666666',
                    },
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: '#002060',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(0, 32, 96, 0.1)',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: '#ccc',
                        opacity: 0.6,
                        borderColor: '#ececec',
                      },
                    },
                    '& .MuiOutlinedInput-input:disabled': {
                      WebkitTextFillColor: '#ccc',
                    },
                    '& .MuiInputBase-input:disabled': {
                      WebkitTextFillColor: '#ccc',
                    },
                    '& .MuiFormLabel-root.Mui-disabled': {
                      color: '#ccc',
                    },
                  }}
                />
              </div>
            )}

            {question.type === 'inputGroup' && (
              <Stack spacing={1.5}>
                {question.options.map((option) =>
                  option.control === 'checkbox' ? (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={Boolean(responses[question.id]?.[option.value])}
                          onChange={(e) => {
                            setFocusedQuestion(question.id);
                            handleInputGroupCheckboxChange(question.id, option.value)(e);
                          }}
                          size="small"
                          disabled={question.id !== 'q1' && !responses.q1}
                          onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                          sx={{
                            '&.Mui-disabled': {
                              opacity: 0.6,
                            },
                          }}
                        />
                      }
                      label={<Typography variant="body2" sx={{ color: question.id !== 'q1' && !responses.q1 ? '#ccc' : '#666', fontWeight: 500 }}>{option.label}</Typography>}
                      disabled={question.id !== 'q1' && !responses.q1}
                      onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                      sx={{
                        m: 0,
                        transition: 'all 0.2s ease-in-out',
                      }}
                    />
                  ) : (
                    <div 
                      key={option.value}
                      onClick={question.id !== 'q1' && !responses.q1 ? () => setRequireQ1Message(true) : undefined}
                      style={{ display: 'block', width: '100%', maxWidth: '400px' }}
                    >
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        label={option.label}
                        value={responses[question.id]?.[option.value] || ''}
                        onChange={(e) => {
                          setFocusedQuestion(question.id);
                          handleInputGroupChange(question.id, option.value)(e);
                        }}
                        onFocus={() => setFocusedQuestion(question.id)}
                        size="small"
                        disabled={question.id !== 'q1' && !responses.q1}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          pointerEvents: question.id !== 'q1' && !responses.q1 ? 'none' : 'auto',
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: '#ffffff',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              borderColor: '#002060',
                            },
                            '&.Mui-focused': {
                              boxShadow: '0 0 0 3px rgba(0, 32, 96, 0.1)',
                            },
                            '&.Mui-disabled': {
                              backgroundColor: '#f5f5f5',
                              color: '#ccc',
                              opacity: 0.6,
                              borderColor: '#ececec',
                            },
                          },
                          '& .MuiOutlinedInput-input:disabled': {
                            WebkitTextFillColor: '#ccc',
                          },
                          '& .MuiInputBase-input:disabled': {
                            WebkitTextFillColor: '#ccc',
                          },
                          '& .MuiFormLabel-root.Mui-disabled': {
                            color: '#ccc',
                          },
                        }}
                      />
                    </div>
                  )
                )}
              </Stack>
            )}

            {question.type === 'dynamicList' && (() => {
              const rows = Array.isArray(responses[question.id]) ? responses[question.id] : [];
              const updateRows = (newRows) => {
                setResponses({ ...responses, [question.id]: newRows });
              };
              const handleAdd = () => {
                setFocusedQuestion(question.id);
                updateRows([...rows, { description: '', price: '' }]);
              };
              const handleRemove = (index) => {
                setFocusedQuestion(question.id);
                updateRows(rows.filter((_, i) => i !== index));
              };
              const handleChange = (index, field, value) => {
                setFocusedQuestion(question.id);
                const next = rows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
                updateRows(next);
              };
              const disabled = !responses.q1;
              return (
                <Stack spacing={1.5}>
                  {rows.map((row, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                      <TextField
                        label={question.descriptionPlaceholder || 'Enter description'}
                        value={row.description || ''}
                        onChange={(e) => handleChange(index, 'description', e.target.value)}
                        onFocus={() => setFocusedQuestion(question.id)}
                        size="small"
                        disabled={disabled}
                        variant="outlined"
                        sx={{ flex: 1, maxWidth: 320 }}
                      />
                      <TextField
                        type="number"
                        inputProps={{ min: 0 }}
                        label={question.pricePlaceholder || 'Enter price'}
                        value={row.price || ''}
                        onChange={(e) => handleChange(index, 'price', e.target.value)}
                        onFocus={() => setFocusedQuestion(question.id)}
                        size="small"
                        disabled={disabled}
                        variant="outlined"
                        sx={{ width: 150 }}
                      />
                      <IconButton
                        aria-label="remove"
                        size="small"
                        onClick={() => handleRemove(index)}
                        disabled={disabled}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <div>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAdd}
                      disabled={disabled}
                      onClickCapture={disabled ? () => setRequireQ1Message(true) : undefined}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      {question.addButtonLabel || 'Add row'}
                    </Button>
                  </div>
                </Stack>
              );
            })()}

            {question.type === 'slider' && (() => {
              const min = typeof question.min === 'number' ? question.min : -100;
              const max = typeof question.max === 'number' ? question.max : 100;
              const step = typeof question.step === 'number' ? question.step : 1;
              const defaultValue = typeof question.defaultValue === 'number' ? question.defaultValue : 0;
              const unit = question.unit || '';
              const raw = responses[question.id];
              const value = typeof raw === 'number'
                ? raw
                : (raw === '' || raw === undefined || raw === null ? defaultValue : Number(raw));
              const disabled = !responses.q1;
              return (
                <Stack spacing={1} sx={{ maxWidth: 500, pt: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="body2" sx={{ minWidth: 40, color: '#666' }}>{min}{unit}</Typography>
                    <Slider
                      value={value}
                      min={min}
                      max={max}
                      step={step}
                      marks={[
                        { value: min, label: '' },
                        { value: defaultValue, label: '' },
                        { value: max, label: '' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v > 0 ? '+' : ''}${v}${unit}`}
                      onChange={(_, newValue) => {
                        setFocusedQuestion(question.id);
                        setResponses({ ...responses, [question.id]: newValue });
                      }}
                      disabled={disabled}
                      sx={{
                        color: '#002060',
                        '& .MuiSlider-thumb': {
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(0, 32, 96, 0.12)',
                          },
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ minWidth: 40, color: '#666', textAlign: 'right' }}>{max}{unit}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 600, color: '#002060' }}>
                    {value > 0 ? '+' : ''}{value}{unit}
                  </Typography>
                </Stack>
              );
            })()}
          </Stack>
        </Paper>
        {question.children && question.children.map((child) => renderQuestion(child, depth + 1))}
      </div>
    );
  };

  // Calculate monthly price using bookkeeping pricing calculator
  const bronzeMonthlyPrice = useMemo(() => {
    return calculateBookkeepingBronzePrice(responses, bookkeepingPricingModifier);
  }, [responses, bookkeepingPricingModifier]);

  const totalMonthlyPrice = useMemo(() => {
    return calculateBookkeepingSilverPrice(responses, bookkeepingPricingModifier);
  }, [responses, bookkeepingPricingModifier]);

  const goldMonthlyPrice = useMemo(() => {
    return calculateBookkeepingGoldPrice(responses, bookkeepingPricingModifier);
  }, [responses, bookkeepingPricingModifier]);

  const serviceCatalogPricing = useSelector((state) => state.responses?.serviceCatalogPricing || 0);
  const combinedTotal = totalMonthlyPrice + serviceCatalogPricing;

  const totalOnceOffFee = useMemo(() => {
    return calculateBookkeepingOnceOffFee(responses, bookkeepingPricingModifier);
  }, [responses, bookkeepingPricingModifier]);

  const serviceCatalogOnceOffFee = useSelector((state) => state.responses?.serviceCatalogOnceOffFee || 0);
  const combinedOnceOffTotal = totalOnceOffFee + serviceCatalogOnceOffFee;

  useEffect(() => {
    if (typeof totalMonthlyPrice === 'number' && !isNaN(totalMonthlyPrice)) {
      dispatch(setQuestionsPricing(totalMonthlyPrice));
    }
  }, [totalMonthlyPrice, dispatch]);
  
  // Re-dispatch pricing when responses or modifier change
  useEffect(() => {
    const recalculatedMonthly = calculateBookkeepingSilverPrice(responses, bookkeepingPricingModifier);
    const recalculatedOnceOff = calculateBookkeepingOnceOffFee(responses, bookkeepingPricingModifier);
    dispatch(setQuestionsPricing(recalculatedMonthly));
    dispatch(setQuestionsOnceOffFee(recalculatedOnceOff));
  }, [responses, bookkeepingPricingModifier, dispatch]);

  useEffect(() => {
    if (typeof totalOnceOffFee === 'number' && !isNaN(totalOnceOffFee)) {
      dispatch(setQuestionsOnceOffFee(totalOnceOffFee));
    }
  }, [totalOnceOffFee, dispatch]);

  return (
    <>
      <Container sx={{ py: 3, pb: 14 }}>
        <Stack spacing={1} sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            Bookkeeping Pricing Calculator
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Answer a few quick questions to calculate bookkeeping pricing for your potential client
          </Typography>

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: { xs: 2, sm: 2.5 },
              borderRadius: '4px',
              backgroundColor: 'rgba(0, 32, 96, 0.03)',
            }}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <TuneIcon sx={{ color: '#002060', fontSize: 20, mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.55 }}>
                  Pricing combines the industry benchmark <strong>Average Hourly Rate</strong> with set fees per service. Change the Average Hourly Rate — every price recalculates instantly.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <BusinessCenterOutlinedIcon sx={{ color: '#002060', fontSize: 20, mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.55 }}>
                  Built for general practice, using benchmark rates across all firm types.
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <ReceiptLongIcon sx={{ color: '#002060', fontSize: 20, mt: '2px' }} />
                <Typography variant="body2" sx={{ color: '#002060', fontWeight: 600 }}>
                  All pricing is GST exclusive
                </Typography>
              </Stack>
              <Divider sx={{ borderColor: 'rgba(0, 32, 96, 0.1)' }} />
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <InfoOutlinedIcon sx={{ color: '#999', fontSize: 18, mt: '2px' }} />
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem', lineHeight: 1.5 }}>
                  <strong>Disclaimer:</strong> The prices generated by this calculator are indicative only and intended as a guide. Actual fees should be tailored to the scope, complexity, and risk profile of each engagement, as well as your firm's cost base, positioning, and client mix. TwentySix Group accepts no liability for pricing decisions made solely on the output of this tool.
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
        {requireQ1Message && (
          <div style={{
            position: 'fixed',
            top: 24,
            left: 0,
            right: 0,
            zIndex: 2000,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            animation: 'slideDown 0.3s ease-out',
          }}>
            <style>{`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-20px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}</style>
            <div style={{ pointerEvents: 'auto', minWidth: 320, maxWidth: 500 }}>
              <Alert 
                severity="warning" 
                onClose={() => setRequireQ1Message(false)}
                sx={{
                  backgroundColor: '#fff3cd',
                  borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Please select an answer for question 2 before proceeding.
                </Typography>
              </Alert>
            </div>
          </div>
        )}
        <Stack spacing={2}>
          {(() => {
            let questionNumber = 0;
            return bookkeepingQuestionData.map((question, index) => {
              // Check if question is visible
              const isVisible = !question.showWhen || question.showWhen(responses);
              if (isVisible) {
                questionNumber++;
              }
              
              const showSectionTitle = question.sectionTitle && (
                index === 0 || bookkeepingQuestionData[index - 1]?.sectionTitle !== question.sectionTitle
              );
              
              // Only render if visible
              if (!isVisible) return null;
              
              return (
                <React.Fragment key={question.id}>
                  {showSectionTitle && (
                    <div style={{ marginTop: index > 0 ? '24px' : 0, paddingTop: index > 0 ? '16px' : 0, borderTop: index > 0 ? '1px solid #e0e0e0' : 'none' }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#002060',
                          fontSize: '1.1rem',
                          mb: 0.5,
                          mt: 0,
                          px: 2,
                          py: 1,
                        }}
                      >
                        {question.sectionTitle}
                      </Typography>
                      {renderQuestion(question, 0, questionNumber)}
                    </div>
                  )}
                  {!showSectionTitle && renderQuestion(question, 0, questionNumber)}
                </React.Fragment>
              );
            });
          })()}
        </Stack>
      </Container>
      
      {/* Sticky Footer Bar with Pricing */}
      <Paper
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          borderTop: '2px solid #e0e0e0',
          boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
          zIndex: 1000,
        }}
      >
        <Container sx={{ py: 2 }}>
          <Stack 
            direction="row" 
            spacing={3}
            sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <Typography variant="body1" sx={{ color: '#666', fontWeight: 500, fontSize: '1.1rem' }}>
              {clientName || 'New Quote'}
            </Typography>
            <Stack direction="row" spacing={3} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Bronze: <span style={{ fontWeight: 700, color: '#cd7f32' }}>${bronzeMonthlyPrice.toFixed(2)}</span>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Silver: <span style={{ fontWeight: 700, color: '#757575' }}>${totalMonthlyPrice.toFixed(2)}</span>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Gold: <span style={{ fontWeight: 700, color: '#d4af37' }}>${goldMonthlyPrice.toFixed(2)}</span>
              </Typography>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Once Off: <span style={{ fontWeight: 700, color: '#002060' }}>${combinedOnceOffTotal.toFixed(2)}</span>
              </Typography>
            </Stack>
            <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  // Final save before navigating
                  if (activePriceId) {
                    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
                    await autoSave(responses);
                  }
                  navigate('/bookkeeping-quote');
                }}
                sx={{
                  flex: { xs: 1, sm: 'initial' },
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 32, 96, 0.4)',
                  },
                }}
              >
                Show Packages
              </Button>
            </Stack>
        </Container>
      </Paper>
    </>
  );
}
